/**
 * ExperimentExplorer — Rich browser for AutoML experiments and candidate runs.
 *
 * Phase 4.10 Track B β4 — WQ-239
 *
 * Replaces the v4.8 placeholder with a filterable, searchable, paginated explorer
 * that consumes the existing AutoML endpoints under /api/admin/ai/automl/. Pattern
 * mirrors WQ-244 LifecycleTimeline (filter strip + search + Load More + click-to-expand).
 *
 * Filters:
 *   - status:        backend filter (sent as query param)
 *   - problem_type:  client-side over fetched batch (backend ignores the param)
 *   - tenant:        client-side, dropdown auto-populated from response
 *   - date range:    client-side over experiment.created_at
 *   - search:        client-side free-text over name + problem_type + dataset id
 *
 * Pagination: backend offset/limit, page size 50, Load More button.
 * Sort:       client-side over current batch (start date desc default; alternatives:
 *             completion date, primary metric).
 * Polling:    30s refetch when statusFilter is empty or 'running'.
 * Champion:   experiment.best_run_id is surfaced as a 🏆 badge on the matching
 *             candidate row when expanded.
 *
 * No new endpoints. No schema changes. No cross-track touches. IL-2 read-only.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon, ChevronDownIcon, ChevronRightIcon } from '../common/Icons';
import {
  listExperiments,
  listRuns,
  compareRuns,
  type Experiment,
  type ExperimentRun,
  type RunComparison,
} from '../../services/automlService';
import { fmt } from '../../utils/formatNumber';

const STATUS_COLORS: Record<string, string> = {
  completed: '#22c55e',
  running: '#3b82f6',
  pending: '#eab308',
  failed: '#dc2626',
  cancelled: '#6b7280',
};

const STATUS_OPTIONS = ['running', 'pending', 'completed', 'failed', 'cancelled'];
const PROBLEM_TYPE_OPTIONS = ['classification', 'regression', 'clustering'];
const PAGE_SIZE = 50;
const POLL_INTERVAL_MS = 30000;

type SortKey =
  | 'created-desc'
  | 'created-asc'
  | 'completed-desc'
  | 'metric-desc'
  | 'metric-asc';

const SORT_LABELS: Record<SortKey, string> = {
  'created-desc': 'Newest first',
  'created-asc': 'Oldest first',
  'completed-desc': 'Recently completed',
  'metric-desc': 'Best metric (desc)',
  'metric-asc': 'Best metric (asc)',
};

function compareExperiments(a: Experiment, b: Experiment, sortBy: SortKey): number {
  const ts = (s: string | null | undefined) => (s ? Date.parse(s) : 0);
  switch (sortBy) {
    case 'created-asc':
      return ts(a.created_at) - ts(b.created_at);
    case 'completed-desc': {
      const av = (a as Experiment & { completed_at?: string | null }).completed_at ?? null;
      const bv = (b as Experiment & { completed_at?: string | null }).completed_at ?? null;
      return ts(bv) - ts(av);
    }
    case 'metric-desc': {
      const av = a.best_metric ?? -Infinity;
      const bv = b.best_metric ?? -Infinity;
      return bv - av;
    }
    case 'metric-asc': {
      const av = a.best_metric ?? Infinity;
      const bv = b.best_metric ?? Infinity;
      return av - bv;
    }
    case 'created-desc':
    default:
      return ts(b.created_at) - ts(a.created_at);
  }
}

const ExperimentExplorer: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [problemTypeFilter, setProblemTypeFilter] = useState('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('created-desc');

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, ExperimentRun[]>>({});
  const [comparison, setComparison] = useState<RunComparison | null>(null);
  const [selectedRuns, setSelectedRuns] = useState<string[]>([]);

  const filtersRef = useRef({ statusFilter });
  filtersRef.current = { statusFilter };

  const fetchData = async (offset: number) => {
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    setError(null);
    try {
      const filters: { status?: string; limit: number; offset: number } = {
        limit: PAGE_SIZE,
        offset,
      };
      if (statusFilter) filters.status = statusFilter;
      const data = await listExperiments(authFetch, filters);
      const safe = Array.isArray(data) ? data : [];
      if (offset === 0) {
        setExperiments(safe);
      } else {
        setExperiments(prev => [...prev, ...safe]);
      }
      setHasMore(safe.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load experiments');
    }
    setLoading(false);
    setLoadingMore(false);
  };

  // Initial load + status filter change → first page
  useEffect(() => {
    if (!authLoading) {
      setExpandedId(null);
      fetchData(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, statusFilter]);

  // Polling for running experiments (30s) when status filter allows running rows
  useEffect(() => {
    if (authLoading) return;
    const includesRunning = statusFilter === '' || statusFilter === 'running';
    if (!includesRunning) return;
    const id = setInterval(() => {
      // Background refresh of first page only — preserves expansion state
      void (async () => {
        try {
          const filters: { status?: string; limit: number; offset: number } = {
            limit: PAGE_SIZE,
            offset: 0,
          };
          if (filtersRef.current.statusFilter) filters.status = filtersRef.current.statusFilter;
          const data = await listExperiments(authFetch, filters);
          const safe = Array.isArray(data) ? data : [];
          setExperiments(prev => {
            // Preserve any rows beyond the first page that may have been Load-More'd
            if (prev.length <= PAGE_SIZE) return safe;
            const tail = prev.slice(PAGE_SIZE);
            return [...safe, ...tail];
          });
        } catch {
          // Silent — polling failures should not surface a banner
        }
      })();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [authLoading, statusFilter, authFetch]);

  // Derived: client-side filtering + sorting
  const filtered = useMemo(() => {
    const fromTs = dateFrom ? Date.parse(dateFrom) : null;
    const toTs = dateTo ? Date.parse(dateTo) + 24 * 3600 * 1000 - 1 : null;
    const q = searchQuery.trim().toLowerCase();
    const out = experiments.filter(e => {
      if (problemTypeFilter && (e.problem_type || '').toLowerCase() !== problemTypeFilter) return false;
      if (tenantFilter) {
        const t = (e as Experiment & { tenant_id?: string | null }).tenant_id ?? '';
        if (t !== tenantFilter) return false;
      }
      if (fromTs !== null) {
        const c = e.created_at ? Date.parse(e.created_at) : 0;
        if (c < fromTs) return false;
      }
      if (toTs !== null) {
        const c = e.created_at ? Date.parse(e.created_at) : 0;
        if (c > toTs) return false;
      }
      if (q) {
        const datasetId = (e as Experiment & { catalog_entry_id?: string }).catalog_entry_id ?? e.dataset_id ?? '';
        const haystack = `${e.name} ${e.problem_type ?? ''} ${datasetId} ${e.id}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
    return [...out].sort((a, b) => compareExperiments(a, b, sortBy));
  }, [experiments, searchQuery, problemTypeFilter, tenantFilter, dateFrom, dateTo, sortBy]);

  const uniqueTenants = useMemo(() => {
    const s = new Set<string>();
    experiments.forEach(e => {
      const t = (e as Experiment & { tenant_id?: string | null }).tenant_id;
      if (t) s.add(t);
    });
    return [...s].sort();
  }, [experiments]);

  const handleExpand = async (expId: string) => {
    if (expandedId === expId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(expId);
    if (!runs[expId]) {
      try {
        const data = await listRuns(authFetch, expId);
        setRuns(prev => ({ ...prev, [expId]: Array.isArray(data) ? data : [] }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load runs');
      }
    }
  };

  const handleCompare = async () => {
    if (selectedRuns.length < 2) return;
    try {
      const result = await compareRuns(authFetch, selectedRuns);
      setComparison(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Comparison failed');
    }
  };

  const toggleRunSelection = (runId: string) => {
    setSelectedRuns(prev =>
      prev.includes(runId) ? prev.filter(r => r !== runId) : [...prev, runId]
    );
  };

  const sortedRunsFor = (expId: string): ExperimentRun[] => {
    const list = runs[expId] ?? [];
    return [...list].sort((a, b) => {
      // Primary metric desc — pick the first metric key consistently across runs
      const aKeys = Object.keys(a.metrics || {});
      const bKeys = Object.keys(b.metrics || {});
      const key = aKeys[0] || bKeys[0];
      if (!key) return 0;
      const av = a.metrics?.[key] ?? -Infinity;
      const bv = b.metrics?.[key] ?? -Infinity;
      return bv - av;
    });
  };

  if (loading && experiments.length === 0) {
    return (
      <div data-testid="experiment-loading" style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading experiments...
      </div>
    );
  }

  return (
    <div data-testid="experiment-explorer">
      {/* Filter strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240, maxWidth: 360 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text"
            placeholder="Search name / dataset / problem type..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            data-testid="experiment-search"
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          data-testid="experiment-status-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={problemTypeFilter}
          onChange={e => setProblemTypeFilter(e.target.value)}
          data-testid="experiment-problem-type-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Problem Types</option>
          {PROBLEM_TYPE_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={tenantFilter}
          onChange={e => setTenantFilter(e.target.value)}
          data-testid="experiment-tenant-filter"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="">All Tenants</option>
          {uniqueTenants.map(t => <option key={t} value={t}>{t.slice(0, 8)}…</option>)}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          data-testid="experiment-date-from"
          aria-label="Date from"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          data-testid="experiment-date-to"
          aria-label="Date to"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        />
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value as SortKey)}
          data-testid="experiment-sort"
          style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          {(Object.keys(SORT_LABELS) as SortKey[]).map(k => (
            <option key={k} value={k}>{SORT_LABELS[k]}</option>
          ))}
        </select>
        {selectedRuns.length >= 2 && (
          <button
            onClick={handleCompare}
            data-testid="experiment-compare"
            style={{
              fontSize: 13, padding: '6px 16px', borderRadius: 6,
              border: 'none', fontWeight: 600, cursor: 'pointer',
              background: 'var(--accent-primary)', color: '#fff',
            }}
          >
            Compare ({selectedRuns.length}) Runs
          </button>
        )}
        <button
          onClick={() => { setExpandedId(null); fetchData(0); }}
          data-testid="experiment-refresh"
          style={{
            fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
            fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div data-testid="experiment-error" style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Comparison panel (preserved from prior iteration) */}
      {comparison && (
        <div style={{
          padding: 14, borderRadius: 8, border: '1px solid var(--border)',
          background: 'var(--bg-secondary)', marginBottom: 16,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 13 }}>Run Comparison</strong>
            <button
              onClick={() => { setComparison(null); setSelectedRuns([]); }}
              data-testid="experiment-comparison-close"
              style={{
                fontSize: 11, padding: '3px 8px', borderRadius: 4,
                border: '1px solid var(--border)', background: 'var(--bg-primary)',
                color: 'var(--text-primary)', cursor: 'pointer',
              }}
            >Close</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Algorithm</th>
                  {Object.keys(comparison.metrics_summary || {}).map(metric => (
                    <th key={metric} style={{ textAlign: 'right', padding: '6px 8px' }}>{metric}</th>
                  ))}
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {comparison.runs.map(run => (
                  <tr key={run.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px', fontWeight: 600 }}>{run.algorithm}</td>
                    {Object.keys(comparison.metrics_summary || {}).map(metric => (
                      <td key={metric} style={{ textAlign: 'right', padding: '6px 8px', fontFamily: 'monospace' }}>
                        {run.metrics[metric]?.toFixed(4) ?? '—'}
                      </td>
                    ))}
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>
                      {run.duration_seconds !== null ? `${run.duration_seconds}s` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div data-testid="experiment-empty" style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>
          {experiments.length === 0
            ? 'No experiments yet. Launch one from the AutoML tab.'
            : 'No experiments match the current filters.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(exp => {
            const isOpen = expandedId === exp.id;
            const expRuns = sortedRunsFor(exp.id);
            const championRunId = exp.best_run_id ?? null;
            return (
              <div
                key={exp.id}
                data-testid={`experiment-row-${exp.id}`}
                style={{ borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden' }}
              >
                <div
                  onClick={() => handleExpand(exp.id)}
                  data-testid={`experiment-header-${exp.id}`}
                  style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '12px 16px', cursor: 'pointer', background: 'var(--bg-secondary)',
                    gap: 12, flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    {isOpen ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                    <strong style={{ fontSize: 14 }}>{exp.name}</strong>
                    <span
                      data-testid={`experiment-status-${exp.id}`}
                      style={{
                        fontSize: 11, padding: '2px 8px', borderRadius: 10,
                        background: `${STATUS_COLORS[exp.status] || '#888'}20`,
                        color: STATUS_COLORS[exp.status] || '#888',
                        fontWeight: 600,
                      }}
                    >{exp.status}</span>
                    {exp.best_run_id && (
                      <span
                        data-testid={`experiment-champion-${exp.id}`}
                        title="This experiment has a championed candidate"
                        style={{
                          fontSize: 10, padding: '2px 6px', borderRadius: 8,
                          background: '#22c55e20', color: '#16a34a', fontWeight: 600,
                        }}
                      >🏆 champion</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                    {exp.problem_type && <span>{exp.problem_type}</span>}
                    <span>{exp.run_count} runs</span>
                    {exp.best_metric !== null && exp.best_metric !== undefined && (
                      <span style={{ fontFamily: 'monospace', color: '#22c55e' }}>
                        Best: {fmt(exp.best_metric, 4)}
                      </span>
                    )}
                    {exp.created_at && (
                      <span>{new Date(exp.created_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div data-testid={`experiment-runs-${exp.id}`} style={{ padding: '8px 16px 16px', background: 'var(--bg-primary)' }}>
                    {!runs[exp.id] ? (
                      <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>Loading runs…</div>
                    ) : expRuns.length === 0 ? (
                      <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>No runs yet.</div>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid var(--border)' }}>
                            <th style={{ textAlign: 'center', padding: '6px 8px', width: 30 }}></th>
                            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Algorithm</th>
                            <th style={{ textAlign: 'center', padding: '6px 8px' }}>Status</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Metrics</th>
                            <th style={{ textAlign: 'right', padding: '6px 8px' }}>Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expRuns.map(run => {
                            const isChampion = run.is_best || run.id === championRunId;
                            return (
                              <tr
                                key={run.id}
                                data-testid={`experiment-run-${run.id}`}
                                style={{ borderBottom: '1px solid var(--border)' }}
                              >
                                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                  <input
                                    type="checkbox"
                                    aria-label={`select-run-${run.id}`}
                                    checked={selectedRuns.includes(run.id)}
                                    onChange={() => toggleRunSelection(run.id)}
                                  />
                                </td>
                                <td style={{ padding: '6px 8px', fontWeight: 500 }}>
                                  {run.algorithm}
                                  {isChampion && (
                                    <span
                                      data-testid={`experiment-run-champion-${run.id}`}
                                      title="Champion candidate"
                                      style={{ marginLeft: 6, fontSize: 11 }}
                                    >🏆</span>
                                  )}
                                </td>
                                <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                                  <span style={{
                                    fontSize: 10, padding: '2px 6px', borderRadius: 8,
                                    background: `${STATUS_COLORS[run.status] || '#888'}20`,
                                    color: STATUS_COLORS[run.status] || '#888',
                                  }}>{run.status}</span>
                                </td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', fontFamily: 'monospace', fontSize: 11 }}>
                                  {Object.entries(run.metrics || {}).map(([k, v]) => `${k}: ${fmt(v, 4)}`).join(', ') || '—'}
                                </td>
                                <td style={{ textAlign: 'right', padding: '6px 8px', fontSize: 12 }}>
                                  {run.duration_seconds !== null ? `${run.duration_seconds}s` : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {hasMore && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <button
                onClick={() => fetchData(experiments.length)}
                disabled={loadingMore}
                data-testid="experiment-load-more"
                style={{
                  fontSize: 13, padding: '8px 16px', borderRadius: 6,
                  border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)', cursor: 'pointer',
                  opacity: loadingMore ? 0.6 : 1,
                }}
              >
                {loadingMore ? 'Loading…' : 'Load More'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExperimentExplorer;
